import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ThumbsUp, MessageSquare, Filter } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const ReviewsPage = () => {
  const featuredReviews = [
    {
      id: 1,
      toolName: 'HubSpot',
      toolLogo: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=100&h=100&fit=crop',
      rating: 5,
      title: 'Best CRM for growing businesses',
      content: 'HubSpot has transformed how we manage customer relationships. The free tier is incredibly generous, and the paid features are worth every penny.',
      author: 'Sarah M.',
      company: 'Tech Startup',
      date: 'Jan 20, 2025',
      helpful: 127
    },
    {
      id: 2,
      toolName: 'Notion',
      toolLogo: 'https://images.unsplash.com/photo-1618477388954-7852f32655ec?w=100&h=100&fit=crop',
      rating: 5,
      title: 'All-in-one workspace that actually works',
      content: 'We migrated from 5 different tools to Notion. It handles our docs, wiki, project management, and databases all in one place.',
      author: 'Michael K.',
      company: 'Design Agency',
      date: 'Jan 18, 2025',
      helpful: 89
    },
    {
      id: 3,
      toolName: 'Slack',
      toolLogo: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=100&h=100&fit=crop',
      rating: 4,
      title: 'Great for team communication',
      content: 'Slack has made our remote team feel more connected. The integrations are fantastic, though it can get noisy without proper channel management.',
      author: 'Emily R.',
      company: 'Marketing Firm',
      date: 'Jan 15, 2025',
      helpful: 64
    },
    {
      id: 4,
      toolName: 'Ahrefs',
      toolLogo: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=100&h=100&fit=crop',
      rating: 5,
      title: 'Essential for serious SEO',
      content: 'The data accuracy and depth of Ahrefs is unmatched. Worth the investment if SEO is crucial to your business.',
      author: 'David L.',
      company: 'E-commerce',
      date: 'Jan 12, 2025',
      helpful: 156
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">User Reviews</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real reviews from real users. See what businesses like yours think about these tools.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 text-center border border-gray-100">
              <div className="text-3xl font-bold text-emerald-600 mb-1">45,000+</div>
              <div className="text-gray-600">Verified Reviews</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center border border-gray-100">
              <div className="text-3xl font-bold text-emerald-600 mb-1">4.6</div>
              <div className="text-gray-600">Average Rating</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center border border-gray-100">
              <div className="text-3xl font-bold text-emerald-600 mb-1">2,500+</div>
              <div className="text-gray-600">Tools Reviewed</div>
            </div>
          </div>

          {/* Featured Reviews */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Featured Reviews</h2>
              <Button variant="outline" size="sm">
                <Filter className="w-4 h-4 mr-2" /> Filter
              </Button>
            </div>

            <div className="space-y-6">
              {featuredReviews.map((review) => (
                <div key={review.id} className="bg-white rounded-xl p-6 border border-gray-100">
                  <div className="flex items-start gap-4">
                    <img 
                      src={review.toolLogo} 
                      alt={review.toolName}
                      className="w-14 h-14 rounded-xl object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <Link to={`/tool/${review.toolName.toLowerCase()}`} className="font-semibold text-gray-900 hover:text-emerald-600">
                            {review.toolName}
                          </Link>
                          <div className="flex items-center gap-1 mt-1">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-4 h-4 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} 
                              />
                            ))}
                          </div>
                        </div>
                        <span className="text-sm text-gray-400">{review.date}</span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">{review.title}</h3>
                      <p className="text-gray-600 mb-4">{review.content}</p>
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          <span className="font-medium text-gray-700">{review.author}</span> from {review.company}
                        </div>
                        <Button variant="ghost" size="sm" className="text-gray-500">
                          <ThumbsUp className="w-4 h-4 mr-1" /> Helpful ({review.helpful})
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Used a tool recently?</p>
            <Button className="bg-emerald-500 hover:bg-emerald-600">
              <MessageSquare className="w-4 h-4 mr-2" /> Write a Review
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReviewsPage;